import React, { forwardRef, lazy, Suspense, useCallback, useContext, useMemo } from 'react';
import { TColor, TIconSize } from '@src/models/common';

import styled from './IconV2.scss';
import { TIconV2Name } from './models';
import { BreakpointsContext } from '@src/providers/Breakpoints';
import { IDefaultLinkProps, TLinkTarget } from '../Link/models';
import { IClassNameProps, IStylesProps } from '@src/models/props';
import { IIdentify } from '@src/models/analytics';
import { classnames } from '@src/utils/classnames';
import { ErrorBoundary } from '../ErrorBoundary';
import { getClasses } from '@src/utils/getClass';
import { getAdaptiveIconsSize } from './utils';
import { Link } from '@src/atoms/Link';

export interface IIconComponentProps extends IClassNameProps, IStylesProps, React.SVGProps<SVGSVGElement> {
  view?: TColor;
}

export interface IIconProps extends IDefaultLinkProps, IIconComponentProps, IIdentify {
  name: TIconV2Name;
  target?: TLinkTarget;
  size?: TIconSize;
  disabled?: boolean;
}

export const IconV2 = forwardRef<SVGSVGElement, IIconProps>(
  ({ name, href, id, view = 'basicBlack', size = 'md', className, disableNextJs, target, disabled, ...props }, ref) => {
    const { breakpoint } = useContext(BreakpointsContext);
    const iconDataId = href && id ? undefined : id;
    const linkIconDataId = id || `icon-${name}`;

    const handleFallback = useCallback((error: Error, errorInfo: React.ErrorInfo) => {
      console.log(errorInfo, error); //eslint-disable-line
    }, []);

    const Component = useMemo(() => {
      const isSvgImport = process.env.LIB_DEV || process.env.NODE_ENV === 'test';
      return lazy(() => import(`./iconsV2/${name}.${isSvgImport ? 'svg' : 'js'}`));
    }, [name]);

    const classes = classnames(
      styled.root,
      styled.size,
      ...getClasses(
        styled,
        {
          view,
        },
        breakpoint,
      ),
      `Icon__name_${name}`,
      !Number.isFinite(size) && styled[`size_${size}`],
      disabled && styled.disabled,
      className,
    );

    const styles = useMemo(() => {
      const sizeStyles = getAdaptiveIconsSize(size, breakpoint);

      return sizeStyles ? { ...sizeStyles, ...props.style } : props.style;
    }, [props.style, size, breakpoint]);

    return (
      <ErrorBoundary fallback={handleFallback} ErrorComponent={<div className={classes} />}>
        <Suspense fallback={<div className={classes} />}>
          <Link.Wrapper id={linkIconDataId} disableNextJs={disableNextJs} target={target} href={href}>
            <Component style={styles} className={classes} {...props} data-id={iconDataId} ref={ref} />
          </Link.Wrapper>
        </Suspense>
      </ErrorBoundary>
    );
  },
);

IconV2.displayName = 'IconV2';
